// Departments page: read-only list of the departments this user can reach, in the two
// ways access exists — serving (devotee membership) and user-account access
// (SysUserDepartment assignments). Data from apiMyDepartmentAccess.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, KeyRound } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Button } from "@/components/ui/button";

import { ApiError, apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface DeptRow {
  id: number;
  name: string | null;
  operationUnitName: string | null;
  accessLevel?: string | null;
}
interface MyDepartmentAccess {
  serving: DeptRow[];
  userAccess: DeptRow[];
}

export default function DepartmentsPage() {
  const { token } = useAuth();
  const [data, setData] = useState<MyDepartmentAccess | null>(null);

  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    apiFetch<MyDepartmentAccess>("/api/myPaymentRequest/apiMyDepartmentAccess", {
      token,
      method: "POST",
      body: {},
      signal: ctrl.signal,
    })
      .then(setData)
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        toast.error(e instanceof ApiError ? e.message : "Could not load your departments.");
        setData({ serving: [], userAccess: [] });
      });
    return () => ctrl.abort();
  }, [token]);

  // Location filter (mandatory — there is no "all locations" in this system): the chips
  // are the distinct locations across both lists, first one preselected.
  const locations = useMemo(() => {
    const all = [...(data?.serving ?? []), ...(data?.userAccess ?? [])]
      .map((d) => d.operationUnitName)
      .filter((n): n is string => !!n);
    return [...new Set(all)];
  }, [data]);
  const [location, setLocation] = useState<string | null>(null);
  useEffect(() => {
    if (location == null && locations.length > 0) setLocation(locations[0]);
  }, [location, locations]);

  if (!data) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-xl" />
        ))}
      </div>
    );
  }

  const inLocation = (d: DeptRow) => location == null || d.operationUnitName === location;

  return (
    <div className="flex flex-col gap-4 p-4">
      {locations.length > 1 && (
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          {locations.map((l) => (
            <Button
              key={l}
              variant={location === l ? "default" : "outline"}
              size="sm"
              className="shrink-0 rounded-full"
              onClick={() => setLocation(l)}
            >
              {l}
            </Button>
          ))}
        </div>
      )}
      <Section
        icon={Building2}
        title="Departments I serve in"
        hint="Where you are a member (via your devotee record)."
        rows={data.serving.filter(inLocation)}
        empty="You are not serving in any department in this location."
      />
      <Section
        icon={KeyRound}
        title="Departments with user access"
        hint="Assigned to your user account (listing access or above)."
        rows={data.userAccess.filter(inLocation)}
        empty="No departments are assigned to your user account in this location."
        showAccessLevel
      />
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  hint,
  rows,
  empty,
  showAccessLevel,
}: {
  icon: LucideIcon;
  title: string;
  hint: string;
  rows: DeptRow[];
  empty: string;
  showAccessLevel?: boolean;
}) {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold">{title}</h2>
        <span className="text-xs text-muted-foreground">({rows.length})</span>
      </div>
      <p className="text-xs text-muted-foreground">{hint}</p>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((d) => (
            <li key={`${title}-${d.id}`}>
              <Card size="sm">
                <CardContent className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">{d.name}</span>
                  <span className="flex flex-wrap items-center gap-1.5">
                    {showAccessLevel && d.accessLevel && (
                      <Badge variant="outline" className="text-muted-foreground capitalize">
                        {d.accessLevel.replace(/_/g, " ").toLowerCase()}
                      </Badge>
                    )}
                    {d.operationUnitName && (
                      <Badge variant="secondary" className="text-muted-foreground">
                        {d.operationUnitName}
                      </Badge>
                    )}
                  </span>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
